import { ReactTable } from "@aix/ui"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { AlertTriangle, CheckCircle, Clock, ExternalLink, Eye, Minus, TrendingDown, TrendingUp } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"

import { trpc } from "../../lib/trpc"

export default function WebsiteChangesTab() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { tab?: string; monitorSlug?: string }
  const [monitorSlug, setMonitorSlug] = useState(search.monitorSlug || "")
  const [requiresReview, setRequiresReview] = useState<boolean | undefined>(undefined)
  const [processed, setProcessed] = useState<boolean | undefined>(undefined)

  // Loading states
  const [approveLoading, setApproveLoading] = useState<string | null>(null)

  const { data: monitors } = trpc.scraping.listMonitors.useQuery()
  const { data, isLoading, refetch } = trpc.scraping.listWebsiteChanges.useQuery(
    {
      monitorSlug: monitorSlug || undefined,
      requiresReview: requiresReview,
      processed: processed,
      limit: 100,
    },
    { keepPreviousData: true },
  )

  const { data: baseline } = trpc.scraping.getWebsiteBaseline.useQuery({ monitorSlug }, { enabled: !!monitorSlug })

  const { mutateAsync: approveChange } = trpc.scraping.approveWebsiteChange.useMutation()

  const handleApproveChange = async (changeId: string) => {
    setApproveLoading(changeId)
    try {
      await approveChange({
        changeId,
        updateBaseline: true,
      })
      toast.success("Change approved and baseline updated")
      void refetch()
    } catch (error) {
      toast.error("Failed to approve change")
      console.error(error)
    } finally {
      setApproveLoading(null)
    }
  }

  const getChangeIcon = (changeType: string) => {
    switch (changeType) {
      case "content_changed":
        return <AlertTriangle className="w-4 h-4 text-amber-500" />
      case "first_run":
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case "no_change":
        return <Minus className="w-4 h-4 text-gray-400" />
      default:
        return <Clock className="w-4 h-4 text-blue-500" />
    }
  }

  const getItemCountChange = (before: number, after: number) => {
    if (before === 0 && after > 0) {
      return { icon: <TrendingUp className="w-4 h-4 text-green-500" />, text: `+${after}`, color: "text-green-700" }
    }
    if (after > before) {
      return {
        icon: <TrendingUp className="w-4 h-4 text-green-500" />,
        text: `+${after - before}`,
        color: "text-green-700",
      }
    }
    if (after < before) {
      return {
        icon: <TrendingDown className="w-4 h-4 text-destructive" />,
        text: `-${before - after}`,
        color: "text-destructive",
      }
    }
    return { icon: <Minus className="w-4 h-4 text-gray-400" />, text: "0", color: "text-gray-500" }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
          <span className="text-primary">Loading website changes...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold  mb-2">Website Changes</h2>
        <p className="text-sm text-primary">Monitor and approve changes detected on tracked websites</p>
      </div>

      {/* Filters and Controls */}
      <div className="bg-foregroundx p-4 rounded-lg space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Monitor</label>
            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={monitorSlug}
              onChange={(e) => setMonitorSlug(e.target.value)}
            >
              <option value="">All monitors</option>
              {monitors
                ?.filter(
                  (m: any) =>
                    // Only show monitors that have website platform
                    m.slug.includes("website") || m.slug.includes("replicate"),
                )
                .map((m: any) => (
                  <option key={m.slug} value={m.slug}>
                    {m.name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Review Status</label>
            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={requiresReview === undefined ? "" : requiresReview.toString()}
              onChange={(e) => setRequiresReview(e.target.value === "" ? undefined : e.target.value === "true")}
            >
              <option value="">All changes</option>
              <option value="true">Requires Review</option>
              <option value="false">No Review Needed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Processed Status</label>
            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={processed === undefined ? "" : processed.toString()}
              onChange={(e) => setProcessed(e.target.value === "" ? undefined : e.target.value === "true")}
            >
              <option value="">All</option>
              <option value="false">Pending</option>
              <option value="true">Processed</option>
            </select>
          </div>

          <button
            className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-background text-primary  transition-colors"
            onClick={() => refetch()}
          >
            Refresh
          </button>
        </div>

        {/* Baseline Info */}
        {baseline && monitorSlug && (
          <div className="pt-4 border-t border-border">
            <div className="bg-blue-50 p-3 rounded-md">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Current Baseline</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <div>Version: {baseline.version}</div>
                <div>Last Updated: {new Date(baseline.createdAt).toLocaleString()}</div>
                <div>Approved By: {baseline.approvedBy}</div>
                {baseline.structuredData && <div>Items: {JSON.parse(baseline.structuredData).models?.length || 0}</div>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Changes Table */}
      <div className="bg-background border border-border rounded-lg overflow-hidden">
        <ReactTable
          data={(data || []).map((change: any) => ({
            id: change.id,
            created: new Date(change.createdAt).toLocaleString(),
            monitor: change.monitorName || change.monitorSlug || "Unknown",
            monitorSlug: change.monitorSlug,
            changeType: change.changeType,
            changeSummary: change.changeSummary,
            itemCountBefore: change.itemCountBefore || 0,
            itemCountAfter: change.itemCountAfter || 0,
            requiresReview: change.requiresReview,
            processed: change.processed,
            processedAt: change.processedAt ? new Date(change.processedAt).toLocaleString() : null,
            jobId: change.jobId,
          }))}
          columns={[
            {
              Header: "Detected",
              accessor: "created",
              Cell: ({ row: { original } }) => <div className="text-sm ">{original.created}</div>,
            },
            {
              Header: "Monitor",
              accessor: "monitor",
              Cell: ({ row: { original } }) => <div className="text-sm ">{original.monitor}</div>,
            },
            {
              Header: "Change Type",
              accessor: "changeType",
              Cell: ({ row: { original } }) => (
                <div className="flex items-center gap-2">
                  {getChangeIcon(original.changeType)}
                  <span className="text-sm capitalize">{original.changeType.replace("_", " ")}</span>
                </div>
              ),
            },
            {
              Header: "Summary",
              accessor: "changeSummary",
              Cell: ({ row: { original } }) => (
                <div className="text-sm  max-w-xs">
                  <div className="truncate" title={original.changeSummary}>
                    {original.changeSummary}
                  </div>
                </div>
              ),
            },
            {
              Header: "Item Count",
              accessor: "itemCountAfter",
              Cell: ({ row: { original } }) => {
                const change = getItemCountChange(original.itemCountBefore, original.itemCountAfter)
                return (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-primary">
                      {original.itemCountBefore} → {original.itemCountAfter}
                    </span>
                    <div className="flex items-center gap-1">
                      {change.icon}
                      <span className={`text-sm font-medium ${change.color}`}>{change.text}</span>
                    </div>
                  </div>
                )
              },
            },
            {
              Header: "Status",
              accessor: "processed",
              Cell: ({ row: { original } }) => {
                if (original.processed) {
                  return (
                    <div className="flex flex-col gap-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Processed
                      </span>
                      {original.processedAt && <span className="text-xs text-gray-500">{original.processedAt}</span>}
                    </div>
                  )
                }

                if (original.requiresReview) {
                  return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      Requires Review
                    </span>
                  )
                }

                return (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-foregroundx text-gray-800">
                    No Action Needed
                  </span>
                )
              },
            },
            {
              Header: "Actions",
              accessor: "id",
              Cell: ({ row: { original } }) => (
                <div className="flex gap-2">
                  {/* View Job Results */}
                  <button
                    className="inline-flex items-center p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    onClick={() =>
                      navigate({
                        to: "/scraping",
                        // @ts-ignore
                        search: { ...search, tab: "results", jobId: original.jobId },
                      })
                    }
                    title="View Job Results"
                  >
                    <Eye className="w-4 h-4" />
                  </button>

                  {/* Open Website */}
                  {original.monitorSlug === "replicate-text-to-image-monitor" && (
                    <button
                      className="inline-flex items-center p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                      onClick={() => window.open("https://replicate.com/collections/text-to-image", "_blank")}
                      title="Open Website"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  )}

                  {/* Approve Change */}
                  {original.requiresReview && !original.processed && (
                    <button
                      className="inline-flex items-center p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleApproveChange(original.id)}
                      disabled={approveLoading === original.id}
                      title="Approve Change & Update Baseline"
                    >
                      {approveLoading === original.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-600 border-t-transparent" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              ),
            },
          ]}
          enableSorting
          enablePagination
        />
      </div>

      {!data?.length && (
        <div className="text-center py-8 text-gray-500">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No website changes found</p>
          <p className="text-sm">Changes will appear here when website monitoring jobs detect differences</p>
        </div>
      )}
    </div>
  )
}
